/**
 * CanvasChatAttachment — File/video upload for Canvas chat input
 *
 * Lightweight attachment button + drop zone that sits next to the chat textarea.
 * Uploads files to GCS via /api/upload, returns gcsUri for inclusion in messages.
 * Supports video, images, CSV, PDF — max 100MB matching MediaUploader limit.
 */

import React, { useState, useRef, useCallback } from 'react';
import { Paperclip, X, Loader2, FileVideo, FileText, Image } from 'lucide-react';
import { C } from '../design-system/primitives/constants';

const API_BASE = process.env.REACT_APP_API_URL || '';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const ACCEPT_TYPES = [
  'video/*', 'image/*',
  'text/csv', 'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

function getFileIcon(mimeType) {
  if (mimeType?.startsWith('video/')) return FileVideo;
  if (mimeType?.startsWith('image/')) return Image;
  return FileText;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CanvasChatAttachment({ attachments, onAttach, onRemove }) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFile = useCallback(async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      alert(`File too large (${formatSize(file.size)}). Max is 100MB.`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress for UX (real progress requires XHR)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 15, 85));
      }, 300);

      const res = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`);

      const result = await res.json();
      setUploadProgress(100);

      onAttach({
        filename: file.name,
        mimeType: file.type,
        size: file.size,
        gcsUri: result.gcsUri || result.uri || result.url,
      });
    } catch (err) {
      console.error('Canvas file upload error:', err);
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 300);
    }
  }, [onAttach]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer?.files;
    if (files?.[0]) uploadFile(files[0]);
  }, [uploadFile]);

  const handleInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files?.[0]) uploadFile(files[0]);
    e.target.value = '';
  }, [uploadFile]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2 px-1">
          {attachments.map((a, i) => {
            const Icon = getFileIcon(a.mimeType);
            return (
              <span
                key={i}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px]"
                style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }}
              >
                <Icon size={12} style={{ color: C.gold }} />
                <span className="max-w-[120px] truncate">{a.filename}</span>
                <span style={{ color: C.t3 }}>{formatSize(a.size)}</span>
                <button
                  onClick={() => onRemove(i)}
                  className="ml-0.5 p-0.5 rounded hover:bg-white/5"
                >
                  <X size={10} style={{ color: C.t3 }} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Upload progress bar */}
      {isUploading && (
        <div className="mb-2 px-1">
          <div className="h-1 rounded-full overflow-hidden" style={{ background: C.s2 }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%`, background: C.gold }}
            />
          </div>
          <p className="text-[9px] mt-1" style={{ color: C.t3 }}>
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}

      {/* Drop zone overlay */}
      {isDragOver && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-xl"
          style={{ background: 'rgba(232,184,0,0.05)', border: '2px dashed #E8B80040' }}
        >
          <p className="text-xs font-medium" style={{ color: C.gold }}>
            Drop file to attach
          </p>
        </div>
      )}

      {/* Attach button */}
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="p-2.5 rounded-xl transition-colors hover:bg-white/[0.03] disabled:opacity-30"
        style={{ color: C.t3 }}
        title="Attach file or video"
      >
        {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_TYPES}
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
}
